/**
 * Session Lifecycle Management
 *
 * Owns the single active Session persisted at STORAGE_KEYS.SESSION.
 * All session mutations go through this module; no other code writes
 * to the session storage key directly.
 *
 * Session type (mirrors design data model):
 *   {
 *     schemaVersion: number,
 *     listId: string,
 *     listName: string,        // snapshot at start
 *     items: string[],         // snapshot at start
 *     startedAt: number,
 *     currentIndex: number,
 *     results: ('yes' | 'no' | null)[],
 *     completed: boolean
 *   } | null
 *
 * Write discipline: every mutation flushes synchronously via safeWrite
 * and returns { session, writeOk } (or { writeOk } for clearSession).
 * Callers (notably the Runner in Task 9) inspect writeOk to surface the
 * design-required "Couldn't save — progress may not survive close" toast
 * on quota / private-browsing failure without reaching past this module
 * into storage directly.
 */

import {
  safeRead,
  safeWrite,
  STORAGE_KEYS,
  CURRENT_SCHEMA_VERSION
} from './storage.js';

/**
 * Read the current session (or null).
 * @returns {object | null}
 */
export function getSession() {
  return safeRead(STORAGE_KEYS.SESSION, null);
}

/**
 * Persist session synchronously.
 * @param {object} session
 * @returns {boolean} true on success, false on quota / private-browsing failure
 */
function persist(session) {
  return safeWrite(STORAGE_KEYS.SESSION, session);
}

function requireSession() {
  const s = getSession();
  if (s === null) {
    throw new Error('No active session');
  }
  return s;
}

/**
 * Start a new session, snapshotting the list's name and items.
 * Overwrites any prior session.
 * @param {{id: string, name: string, items: string[]}} list
 * @returns {{session: object, writeOk: boolean}}
 */
export function startSession(list) {
  const session = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    listId: list.id,
    listName: list.name,
    items: [...list.items],
    startedAt: Date.now(),
    currentIndex: 0,
    results: list.items.map(() => null),
    completed: false
  };
  const writeOk = persist(session);
  return { session, writeOk };
}

/**
 * Record the answer at currentIndex and advance.
 * No-op if currentIndex is already at items.length (guards against
 * a late double-tap after the final item). A no-op still flushes to
 * keep the returned writeOk meaningful (true for a pure no-op since
 * nothing needed to be written).
 * @param {'yes' | 'no'} answer
 * @returns {{session: object, writeOk: boolean}}
 */
export function recordAnswer(answer) {
  if (answer !== 'yes' && answer !== 'no') {
    throw new Error('recordAnswer requires "yes" or "no"');
  }
  const s = requireSession();
  if (s.currentIndex >= s.items.length) {
    return { session: s, writeOk: true };
  }
  s.results[s.currentIndex] = answer;
  s.currentIndex += 1;
  const writeOk = persist(s);
  return { session: s, writeOk };
}

/**
 * Step back one item, clearing the prior answer.
 * No-op at index 0.
 * Also clears the completed flag if the session was completed,
 * since the last answered slot is being un-recorded.
 * @returns {{session: object, writeOk: boolean}}
 */
export function backOneItem() {
  const s = requireSession();
  if (s.currentIndex === 0) {
    return { session: s, writeOk: true };
  }
  s.currentIndex -= 1;
  s.results[s.currentIndex] = null;
  if (s.completed) {
    s.completed = false;
  }
  const writeOk = persist(s);
  return { session: s, writeOk };
}

/**
 * Mark the session complete. Called by the Runner when the final
 * answer is recorded and currentIndex reaches items.length.
 * @returns {{session: object, writeOk: boolean}}
 */
export function completeSession() {
  const s = requireSession();
  s.completed = true;
  const writeOk = persist(s);
  return { session: s, writeOk };
}

/**
 * Set the result at a specific index. Used by Summary's tap-to-toggle
 * to flip a yes/no without re-running the entire list.
 *
 * Flushes synchronously to storage inside safeWrite, returning
 * { session, writeOk } for the caller to surface the design-required
 * "Couldn't save — progress may not survive close" toast on false.
 *
 * @param {number} index
 * @param {'yes'|'no'} value
 * @returns {{ session: object | null, writeOk: boolean }}
 */
export function setResultAtIndex(index, value) {
  const session = getSession();
  if (!session) {
    return { session: null, writeOk: false };
  }
  if (!Number.isInteger(index) || index < 0 || index >= session.items.length) {
    throw new Error(`setResultAtIndex: index out of bounds: ${index}`);
  }
  if (value !== 'yes' && value !== 'no') {
    throw new Error(`setResultAtIndex: value must be "yes" or "no", got: ${value}`);
  }
  session.results = session.results.slice();
  session.results[index] = value;
  const writeOk = persist(session);
  return { session, writeOk };
}

/**
 * Remove the session entirely. Idempotent.
 * Uses localStorage.removeItem directly so getSession returns null
 * (safeRead on a missing key returns fallback null) and the stored
 * key is actually removed rather than left as the literal string "null".
 * @returns {{writeOk: boolean}} writeOk=false only if removeItem throws
 *                                (private-browsing / quota edge); in-memory
 *                                state is still considered cleared.
 */
export function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEYS.SESSION);
    return { writeOk: true };
  } catch (e) {
    // Private-browsing / quota edge: swallow; state in memory is already gone.
    return { writeOk: false };
  }
}

/**
 * True when an incomplete session exists for the given listId.
 * Used by Home to branch the tap-to-run flow (same-list resume).
 * @param {string} listId
 * @returns {boolean}
 */
export function hasActiveSessionForList(listId) {
  const s = getSession();
  return s !== null && !s.completed && s.listId === listId;
}

/**
 * True when any incomplete session exists.
 * Used by Home to branch the tap-to-run flow (different-list prompt).
 * @returns {boolean}
 */
export function hasAnyActiveSession() {
  const s = getSession();
  return s !== null && !s.completed;
}
